import appConfig from "../../appconfig";
import { store } from "../../store";
import { BifrostBackendApi, BifrostBackendNestApi } from "../../gateways/BifrostBackendApi";

const bifrostBackend = (useNewEndPointForcefully = false) => {
	const useNewSubscriptionEndPoint = store.getters.subscriptionExistsData.useNewSubscriptionEndPoint || useNewEndPointForcefully;
	return useNewSubscriptionEndPoint ? BifrostBackendNestApi : BifrostBackendApi;
};

const SubscriptionProvider = {
	async setSubscriptionProvider(idpToken, providerName, countryCode = "dk") {
		const params = {
			tenantId: appConfig.tenantId,
			countryCode,
			providerName
		};

		return BifrostBackendApi.post(
			`/subscriptions/set-provider`,
			params,
			{
				headers: {
					Authorizationidp: idpToken
				}
			})
			.then(({ data }) => data);
	},
	async subscriptionExists(idpToken) {
		const idpManagementQueryParams = {
			tenantId: appConfig.tenantConfig.tenantId
		};
		const subscriptionExists = await BifrostBackendNestApi.get("/subscriptions/exists", {
			params: idpManagementQueryParams,
			headers: {
				Authorizationidp: idpToken
			}
		});
		if (typeof subscriptionExists.data === "object") {
			return subscriptionExists.data;
		}
		return BifrostBackendApi.get("/subscriptions/exists", {
			params: idpManagementQueryParams,
			headers: {
				Authorizationidp: idpToken
			}
		}).then(({ data }) => data);
	},
	async initializeSubscription(idpToken, countryCode, urlAccept, urlCancel, urlCheckout, planId, additionalInvoiceDetails, additionalPaymentDetails, deviceData) {
		const clientTraceIdFromStorage = localStorage.getItem("clientTraceId");
		const initializeSubscriptionBody = {
			tenantId: appConfig.tenantId,
			countryCode,
			urlAccept,
			urlCancel,
			urlCheckout,
			planId,
			additionalInvoiceDetails,
			additionalPaymentDetails,
			clientTraceId: clientTraceIdFromStorage ? clientTraceIdFromStorage : "",
			deviceData
		};

		// for new subscriptions, we will always use the new v2 end point
		// to make sure subscription provider is billwerkplus
		return bifrostBackend(store.getters.featureToggles.useBillwerkPlus).post(
			`/subscriptions/initialize`,
			initializeSubscriptionBody,
			{
				headers: {
					Authorizationidp: idpToken
				}
			})
			.then(({ data }) => data);
	},
	// TODO
	// this method should be renamed to updateSubscriptionPaymentMethod
	// also the new endpoint in nest should be updated to eg /subscriptions/update-payment-method
	async initializeUpdateSubscription(idpToken, urlAccept, urlCancel, urlCheckout, planId, paymentMethod, additionalPaymentDetails) {
		const initializeUpdateSubscriptionBody = {
			tenantId: appConfig.tenantId,
			urlAccept,
			urlCancel,
			urlCheckout,
			planId,
			paymentMethod,
			additionalPaymentDetails
		};

		return bifrostBackend().put(
			`/subscriptions/initialize/update`,
			initializeUpdateSubscriptionBody,
			{
				headers: {
					Authorizationidp: idpToken
				}
			})
			.then(({ data }) => data);
	},
	async activateSubscriptionPlan(token, body) {
		return BifrostBackendApi.post(
			`/subscriptions/activate-plan`,
			{
				tenantId: appConfig.tenantId,
				...body
			},
			{
				headers: {
					Authorizationidp: token
				}
			})
			.then(({ data }) => data);
	},
	async updateSubscriptionPlan(token, body) {
		return BifrostBackendApi.post(
			`/subscriptions/update-plan`,
			{
				tenantId: appConfig.tenantId,
				...body
			},
			{
				headers: {
					Authorizationidp: token
				}
			})
			.then(({ data }) => data);
	},
	async listSubscriptionPlans(useNewSubscriptionEndPoint) {
		return bifrostBackend(useNewSubscriptionEndPoint).get(
			`/subscriptions/plans?tenantId=${appConfig.tenantId}`
		).then(({ data }) => data);
	},
	async listInvoicesByCustomer(token) {
		return bifrostBackend().get(
			`/subscriptions/invoices?tenantId=${appConfig.tenantId}`, {
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async getInvoicePdfById(token, invoiceId) {
		return BifrostBackendApi.get(
			`/subscriptions/Invoices/${invoiceId}?tenantId=${appConfig.tenantId}`, {
				responseType: store.getters.subscriptionExistsData.useNewSubscriptionEndPoint ? undefined : "blob",
				headers: {
					Authorizationidp: token
				}
			}
		).then(( response ) => {
			return {
				pdfData: response.data,
				fileName: response.headers["content-disposition"].split("filename=")[1].split(";")[0]
			};
		});
	},
	async getSubscriptionPlanById(planId) {
		return bifrostBackend().get(
			`/subscriptions/plans/${planId}?tenantId=${appConfig.tenantId}`
		).then(({ data }) => data);
	},
	async changeSubscriptionState(token, shopId, type) {
		return bifrostBackend().post(
			`/subscriptions/state?tenantId=${appConfig.tenantId}${shopId ? `&shopId=${shopId}` : ""}&type=${type}`,
			{
				tenantId: appConfig.tenantId
			},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async cancelSubscription(token, shopId) {
		// TODO
		// I noticed that we are not using the shopId or the subscriptionConfig in the params provided.
		// may be at sometime it was being used but with the current implementation I did not see these values
		// being used. If this is the case, when we shift to billwerk completely, we need to remove the shopId
		// and the subscriptionConfig url params.
		return bifrostBackend().post(
			`/subscriptions/cancel?tenantId=${appConfig.tenantId}${shopId ? `&shopId=${shopId}` : ""}`,
			{
				subscriptionConfig: {
					canceloption: 2
				}
			},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async switchSubscriptionPlan(token, switchSubscriptionPlanConfig) {
		return bifrostBackend().post(
			`/subscriptions/switch-plan?tenantId=${appConfig.tenantId}`,
			{
				config: switchSubscriptionPlanConfig
			},
			{
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	async getPaymentMethodStatus(token, shopId) {
		return BifrostBackendApi.get(
			`/subscriptions/payment-methods`,
			{
				params: {
					tenantId: appConfig.tenantId,
					shopId
				},
				headers: {
					Authorizationidp: token
				}
			}
		).then(({ data }) => data);
	},
	/**
	 * TODO
	 * We can remove this once billwerk is completely implemented.
	 */
	async saveNetsPayment({ authorization, tenantId, customerId, planId, paymentid }) {
		if(!authorization || !tenantId || !customerId || !planId || !paymentid) {
			return;
		}

		return BifrostBackendApi.post("/subscriptions/webhooks/nets", 
			{ paymentId: paymentid },
			{
				params: {
					tenantId,
					customerId,
					planId
				}, headers: {
					authorization
				}
			}
		).then(({ data }) => data);
	}
};

export default SubscriptionProvider;
